import * as Dialog from '@radix-ui/react-dialog'
import { ExternalLink, X } from 'lucide-react'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Button } from '@/components/common/Button'
import { fromAzureLocation } from '@/config/azureRegions'
import { ENVIRONMENT_LABELS } from '@/config/workwise'
import type { DeploymentRequest } from '@/types'
import type { WorkwiseServerEntry, WorkwiseSummary } from '@/types/workwise'
import { formatDateTime } from '@/utils/format'
import { externalLinkProps, safeHttpUrl } from '@/utils/safeUrl'
import { formatUsd } from '@/utils/workwise/cost'
import { summaryModelLabel } from '@/utils/workwise/model'
import { HostLink, ModePill, ProvisioningPill, SafeLink, TerraformPill } from './DeploymentCells'
import styles from './deployments.module.css'

const ROLE_LABEL: Record<WorkwiseServerEntry['role'], string> = {
  app: 'App server',
  db: 'Database server',
  gateway: 'Gateway server',
}

/** "vnet-x / subnet-x", "pending" while the shared server's network is not recorded, "—" for older rows. */
function networkText(network: WorkwiseSummary['network']): string {
  if (!network) return '—'
  if (network.pending) return 'pending (not recorded on the shared server yet)'
  const names = [network.vnet, network.subnet].filter(Boolean).join(' / ') || '—'
  return network.source === 'existing_network' ? `${names} (existing network)` : names
}

/** Revision 4: the Dedicated address ranges, "VNet 10.20.0.0/24 · subnet 10.20.0.0/27"; null when the row has none. */
function cidrText(network: WorkwiseSummary['network']): string | null {
  const parts = [
    network?.vnet_cidr ? `VNet ${network.vnet_cidr}` : '',
    network?.subnet_cidr ? `subnet ${network.subnet_cidr}` : '',
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/** Servers from the API, or a fallback built from the summary when the list is absent. */
function serversOf(ww: WorkwiseSummary): WorkwiseServerEntry[] {
  if (ww.servers?.length) return ww.servers
  return [
    { role: 'app', mode: 'dedicated', name: ww.app_server.name, sku: ww.app_server.sku, size: null, shared: false, private_ip: null, public_ip: null, status: '', resource_id: null },
    { role: 'db', mode: ww.db_mode, name: ww.db_server.name, sku: ww.db_server.sku, size: null, shared: ww.db_server.shared, private_ip: null, public_ip: null, status: '', resource_id: null },
    { role: 'gateway', mode: ww.gateway_mode, name: ww.gateway_server.name, sku: null, size: null, shared: ww.gateway_server.shared, private_ip: null, public_ip: null, status: '', resource_id: null },
  ]
}

function ServerCard({ server }: { server: WorkwiseServerEntry }) {
  const size = [server.sku, server.size].filter(Boolean).join(' / ')
  return (
    <div className={styles.serverCard} data-testid={`drawer-server-${server.role}`}>
      <div className={styles.serverHead}>
        <span className={styles.serverRole}>{ROLE_LABEL[server.role]}</span>
        <ModePill mode={server.mode} />
      </div>
      <span className={styles.serverName}>{server.name}</span>
      <dl className={styles.kv}>
        <dt>{server.shared ? 'Size' : 'SKU / size'}</dt>
        <dd>{size || '—'}</dd>
        <dt>Private IP</dt>
        <dd className={styles.mono}>{server.private_ip ?? '—'}</dd>
        {server.public_ip && (
          <>
            <dt>Public IP</dt>
            <dd className={styles.mono}>{server.public_ip}</dd>
          </>
        )}
        <dt>Status</dt>
        <dd>{server.status || '—'}</dd>
      </dl>
    </div>
  )
}

interface DeploymentDrawerProps {
  deployment: DeploymentRequest | null
  onClose: () => void
  onOpenDetails: (dep: DeploymentRequest) => void
}

/** Right-hand drawer for a WorkWise row: the three servers, key facts and links. */
export function DeploymentDrawer({ deployment, onClose, onOpenDetails }: DeploymentDrawerProps) {
  const ww = deployment?.workwise ?? null
  const open = deployment !== null && ww !== null
  const pipelineLinks = deployment
    ? ([
        ['Infrastructure run', deployment.pipeline_run_url],
        ['App deploy run', deployment.app_pipeline_run_url],
      ] as const).filter(([, url]) => safeHttpUrl(url))
    : []

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} data-testid="deployment-drawer">
          {deployment && ww && (
            <>
              <div className={styles.drawerHeader}>
                <div>
                  <Dialog.Title className={styles.drawerTitle}>{deployment.customer_name}</Dialog.Title>
                  <Dialog.Description className={styles.drawerSubtitle}>
                    Customer #{ww.customer_number} - {ENVIRONMENT_LABELS[ww.environment]} - {fromAzureLocation(deployment.azure_region)}
                  </Dialog.Description>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <StatusBadge status={deployment.status} />
                    {deployment.provisioning_status && <ProvisioningPill status={deployment.provisioning_status} />}
                    <TerraformPill status={ww.terraform_state_status} />
                  </div>
                </div>
                <Dialog.Close asChild>
                  <button type="button" className={styles.drawerClose} aria-label="Close" data-testid="drawer-close">
                    <X size={16} />
                  </button>
                </Dialog.Close>
              </div>

              <div className={styles.drawerBody}>
                <section className={styles.drawerSection}>
                  <h3 className={styles.drawerSectionTitle}>Servers ({ww.vm_count} VM{ww.vm_count === 1 ? '' : 's'} created)</h3>
                  {serversOf(ww).map((server) => (
                    <ServerCard key={server.role} server={server} />
                  ))}
                </section>

                <section className={styles.drawerSection}>
                  <h3 className={styles.drawerSectionTitle}>Details</h3>
                  <dl className={styles.kv}>
                    <dt>Host name</dt>
                    <dd data-testid="drawer-hostname">
                      <HostLink host={deployment.hostname} />
                    </dd>
                    <dt>Version</dt>
                    <dd className={styles.mono}>{ww.app_version || '—'}</dd>
                    <dt>Est. cost / mo</dt>
                    <dd>{ww.est_monthly_cost ? formatUsd(Number(ww.est_monthly_cost)) : '—'}</dd>
                    <dt>Owner</dt>
                    <dd>{ww.owner || '—'}</dd>
                    <dt>Deployment model</dt>
                    <dd data-testid="drawer-model">{summaryModelLabel(ww) ?? '—'}</dd>
                    <dt>Network</dt>
                    <dd className={styles.mono} data-testid="drawer-network">
                      {networkText(ww.network)}
                    </dd>
                    {cidrText(ww.network) && (
                      <>
                        <dt>Address space</dt>
                        <dd className={styles.mono} data-testid="drawer-network-cidr">
                          {cidrText(ww.network)}
                        </dd>
                      </>
                    )}
                    <dt>Resource group</dt>
                    <dd className={styles.mono}>{ww.resource_group ?? '—'}</dd>
                    <dt>Created</dt>
                    <dd>
                      {formatDateTime(deployment.created_at)}
                      {deployment.created_by ? ` by ${deployment.created_by}` : ''}
                    </dd>
                    <dt>Correlation ID</dt>
                    <dd className={styles.mono}>{ww.correlation_id}</dd>
                  </dl>
                </section>

                {(safeHttpUrl(ww.azure_portal_url) || pipelineLinks.length > 0) && (
                  <section className={styles.drawerSection}>
                    <h3 className={styles.drawerSectionTitle}>Links</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                      {safeHttpUrl(ww.azure_portal_url) && <SafeLink href={ww.azure_portal_url}>Open in Azure portal</SafeLink>}
                      {pipelineLinks.map(([label, url]) => (
                        <a key={label} {...externalLinkProps(safeHttpUrl(url) as string)} className={styles.link}>
                          {label} <ExternalLink size={11} style={{ verticalAlign: 'middle' }} />
                        </a>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              <div className={styles.drawerFooter}>
                <Button data-testid="drawer-open-details" onClick={() => onOpenDetails(deployment)}>
                  Open details
                </Button>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
